// ═══════════════════════════════════════════════════════════════════════════════
// MoonbeamCreatorFinalize.js
// ═══════════════════════════════════════════════════════════════════════════════
//
// Step 2 of the send flow — "Finalize & Share". Final review screen where
// the user confirms what's about to happen and clicks Share.
//
// This screen is reached only when EMAIL is part of the share. Link-only
// shares skip it entirely (Step 1's "Share" button sends directly). So the
// review surface here always has the email panel, optionally alongside the
// link panel.
//
// CHANGES IN THIS REVISION:
//   - Display text "eCard"→"Moonbeam" in the user-facing strings (upgrade
//     banner message + the paid-plan button message). No structural change.
//
// PREVIOUS REVISIONS — preserved:
//   - One unified panel per channel, gold ChannelDivider between them.
//   - hasEmail = emailEnabled && recipients.length > 0.
//   - From Name editor + Recipients list inline within the email panel.
//   - Recipients preview limit of 3 with "View all" expander.
// ═══════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import Button from '../ui/Button.js';
import { isUnlimitedUsage, UpgradeBanner } from './MoonbeamCreatorShared.js';

// ─── Channel divider ─────────────────────────────────────────────────────────
function ChannelDivider() {
  return (
    <div className="flex items-center gap-3 py-2" role="separator">
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gold/40 to-gold/40" />
      <span className="text-gold text-base" aria-hidden="true">✦</span>
      <div className="flex-1 h-px bg-gradient-to-l from-transparent via-gold/40 to-gold/40" />
    </div>
  );
}

export default function FinalizeStep({
  templateData, recipients, deliveryMode, scheduleDate, birthdayTime,
  canSend, isLoggedIn, sending, onSend, error,
  siteName, usage,
  fromName, setFromName, defaultFromName,
  templateName,
  linkEnabled, linkLabel,
  emailEnabled,
  onBack,
}) {
  const hasEmail = emailEnabled && recipients.length > 0;
  const hasLink = !!linkEnabled;
  const hasAnyChannel = hasEmail || hasLink;
  const showDivider = hasLink && hasEmail;

  const unlimited = isUnlimitedUsage(usage);
  const exceedsLimit = !unlimited && usage && hasEmail && recipients.length > usage.remaining;

  const [showAllRecipients, setShowAllRecipients] = useState(false);
  const RECIPIENT_PREVIEW_LIMIT = 3;
  const hasMoreRecipients = recipients.length > RECIPIENT_PREVIEW_LIMIT;
  const visibleRecipients = showAllRecipients
    ? recipients
    : recipients.slice(0, RECIPIENT_PREVIEW_LIMIT);

  const trimmedFromName = (fromName || '').trim();
  const fromNameIsBlank = trimmedFromName.length === 0;

  const needsScheduleDate = hasEmail
    && deliveryMode === 'schedule'
    && (!scheduleDate || new Date(scheduleDate) <= new Date());

  const sendDisabled =
    sending
    || !canSend
    || !hasAnyChannel
    || needsScheduleDate
    || exceedsLimit;

  let buttonMessage = '';
  if (!isLoggedIn) buttonMessage = 'Create an account to share Moonbeams';
  else if (!canSend) buttonMessage = 'This Moonbeam requires a paid plan';
  else if (!hasAnyChannel) buttonMessage = 'Go back and turn on a sharing method';
  else if (needsScheduleDate) buttonMessage = 'Set a future date for the scheduled email send in the previous step';
  else if (exceedsLimit) buttonMessage = `Too many email recipients. Remove ${recipients.length - usage.remaining} to continue.`;

  const emailWhenDisplay = (() => {
    if (!hasEmail) return null;
    if (deliveryMode === 'now') return 'Send Now';
    if (deliveryMode === 'schedule') {
      if (scheduleDate) return new Date(scheduleDate).toLocaleString();
      return <span className="text-red-400">No date set</span>;
    }
    if (deliveryMode === 'birthday') return `Each birthday at ${birthdayTime}`;
    return null;
  })();

  const linkLabelDisplay = (() => {
    if (!hasLink) return null;
    const label = (linkLabel || '').trim();
    if (label) {
      return <span className="text-white font-medium">{label}</span>;
    }
    const fallback = (templateName || '').trim() || 'Shareable link';
    return <span className="text-white font-medium italic">{fallback}</span>;
  })();

  const linkLabelRowHeading = (linkLabel || '').trim()
    ? 'Label (custom note)'
    : 'Label';

  const emailLabel = `Email by ${siteName || 'The Cherry Fairy Shop'}`;

  return (
    <div className="space-y-4">
      {!isLoggedIn && <UpgradeBanner isLoggedIn={false} message="Create an account to share Moonbeams" />}

      <div className={!isLoggedIn ? 'opacity-60 pointer-events-none' : ''}>
        {/* ── Navigation: Back / Share button ──────────────────────────────── */}
        <div className="flex items-center justify-between mb-4">
          <button onClick={onBack} className="flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base text-white hover:text-gold transition-colors">
            <span>←</span><span>How to Share</span>
          </button>
          <Button onClick={onSend} disabled={sendDisabled}>
            {sending ? 'Processing...' : 'Share'}
          </Button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="p-3 mb-4 bg-red-900/30 border border-red-700 rounded-xl text-red-400 text-base">{error}</div>
        )}

        {/* Contextual help when the button is disabled */}
        {buttonMessage && (
          <p className="text-center text-royal-100 text-sm mb-4">
            {(!isLoggedIn || !canSend) ? (
              <>{buttonMessage} — <a href={isLoggedIn ? '/pricing' : '/register'} className="text-gold hover:text-gold-light transition-colors underline">{isLoggedIn ? 'View Plans' : 'Create Account'}</a></>
            ) : buttonMessage}
          </p>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* CHANNEL PANELS                                                     */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {hasAnyChannel && (
          <div className="mb-4">

            {/* 🔗 LINK PANEL */}
            {hasLink && (
              <div className="bg-royal-900/40 border border-royal-600 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 sm:px-5 py-3 bg-royal-800/30 border-b border-royal-700/40">
                  <span className="text-xl" aria-hidden="true">🔗</span>
                  <h3 className="text-gold font-semibold text-base">Shareable Link</h3>
                </div>
                <div className="px-4 sm:px-5 py-4 space-y-2 text-base">
                  <div className="flex justify-between items-center gap-3">
                    <span className="text-royal-100">{linkLabelRowHeading}</span>
                    <span className="text-right">{linkLabelDisplay}</span>
                  </div>
                  <div className="flex justify-between items-start gap-3">
                    <span className="text-royal-100">Where</span>
                    <span className="text-white text-right text-sm sm:text-base">
                      You share it — text, social, anywhere
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Gold gradient divider — only between the two panels. */}
            {showDivider && <ChannelDivider />}

            {/* ✉️ EMAIL PANEL — unified, with inline From Name + Recipients */}
            {hasEmail && (
              <div className="bg-royal-900/40 border border-royal-600 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 sm:px-5 py-3 bg-royal-800/30 border-b border-royal-700/40">
                  <span className="text-xl" aria-hidden="true">✉️</span>
                  <h3 className="text-gold font-semibold text-base">{emailLabel}</h3>
                </div>
                <div className="px-4 sm:px-5 py-4 text-base">

                  {/* Top key/value rows */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center gap-3">
                      <span className="text-royal-100">Recipients</span>
                      <span className="text-white font-medium text-right">
                        {recipients.length} {recipients.length === 1 ? 'person' : 'people'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-3">
                      <span className="text-royal-100">When</span>
                      <span className="text-white font-medium text-right">
                        {emailWhenDisplay}
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-3">
                      <span className="text-royal-100">From</span>
                      <span className="text-white font-medium text-right">
                        {trimmedFromName || defaultFromName || 'Someone Special'}
                      </span>
                    </div>
                  </div>

                  {/* Inline From Name editor */}
                  <div className="pt-4 mt-4 border-t border-royal-700/40">
                    <p className="text-royal-100 text-sm font-semibold uppercase tracking-wider mb-2">
                      From Name
                    </p>
                    <p className="text-royal-100 text-sm mb-2 leading-relaxed">
                      How would you like to appear in the email? Change this if you
                      want something different — like <span className="text-white">Grandma Jones</span>,{' '}
                      <span className="text-white">Dad</span>, or <span className="text-white">Aunt Carol</span>.
                    </p>
                    <input
                      id="fromName"
                      type="text"
                      value={fromName || ''}
                      onChange={(e) => setFromName(e.target.value)}
                      placeholder={defaultFromName || 'Your name'}
                      className="w-full px-3 py-2.5 bg-royal-900/60 border border-royal-600 rounded-lg text-white text-base placeholder-royal-100 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors"
                    />
                    <div className="flex items-center justify-between mt-2 gap-3">
                      <p className="text-royal-100 text-sm flex-1">
                        {fromNameIsBlank
                          ? <>Defaults to <span className="text-white">{defaultFromName || 'your account name'}</span> if left blank.</>
                          : <>This name will appear in the email subject and body.</>}
                      </p>
                      {!fromNameIsBlank && trimmedFromName !== defaultFromName && defaultFromName && (
                        <button
                          type="button"
                          onClick={() => setFromName(defaultFromName)}
                          className="text-sm text-gold hover:text-gold-light transition-colors underline whitespace-nowrap"
                        >
                          Reset to {defaultFromName}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Inline recipients list */}
                  <div className="pt-4 mt-4 border-t border-royal-700/40">
                    <p className="text-royal-100 text-sm font-semibold uppercase tracking-wider mb-2">
                      Recipients ({recipients.length})
                    </p>
                    <div className="space-y-0">
                      {visibleRecipients.map((r, i) => (
                        <div
                          key={i}
                          className="text-base py-1.5 border-b border-royal-700/30 last:border-0"
                        >
                          <span className="text-white">{r.name}</span>
                          {r.email && <span className="text-royal-100 ml-1">— {r.email}</span>}
                        </div>
                      ))}
                    </div>
                    {hasMoreRecipients && (
                      <button
                        type="button"
                        onClick={() => setShowAllRecipients((v) => !v)}
                        className="mt-2 text-sm text-gold hover:text-gold-light transition-colors underline"
                      >
                        {showAllRecipients
                          ? 'Show less'
                          : `View all ${recipients.length} recipients`}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty state — defensive backstop. Step 1 prevents this in normal flow. */}
        {!hasAnyChannel && (
          <div className="bg-royal-900/40 border border-royal-600 rounded-xl px-4 sm:px-5 py-4 text-center">
            <p className="text-royal-100 text-sm">
              Nothing to share yet. Go back and turn on a sharing method.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}